import React, { useState, useEffect, useCallback } from 'react';
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Avatar,
  CircularProgress,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Assignment as AssignmentIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Star as StarIcon,
  Chat as ChatIcon,
  MarkChatRead as MarkChatReadIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../utils/dateLocale';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

interface Notification {
  id: number;
  type: 'new_bid' | 'task_created' | 'bid_accepted' | 'bid_rejected' | 'task_completed' | 'review_received' | 'message_received' | 'chat_started';
  title: string;
  message: string;
  is_read: number;
  created_at: string;
  link?: string;
  sender_id?: number;
  sender_name?: string;
  task_id?: number;
}

// window.Notification (Web Notifications API) не съществува в обикновен
// браузърен таб на iOS Safari/Chrome (WebKit) - само в installed PWA от iOS
// 16.4+. Директен достъп до `Notification.permission`/`Notification.requestPermission`
// без тази проверка хвърля ReferenceError и чупи целия React tree на iOS.
const isNotificationApiSupported = (): boolean =>
  typeof window !== 'undefined' && 'Notification' in window;

const NotificationBell: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { socket, isConnected, lastError, reconnect } = useWebSocket();
  const { isAuthenticated, logout } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    setLoading(true);
    setLoadError(null);
    try {
      const response = await api.get('/api/v1/notifications');
      setNotifications(response.data);
      setUnreadCount(response.data.filter((notification: Notification) => !notification.is_read).length);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;

        // При изтекла/невалидна сесия спираме flood от 403 и излизаме.
        if (status === 401 || status === 403) {
          setNotifications([]);
          setUnreadCount(0);
          setLoadError(t('auth.session_expired'));
          logout();
          navigate('/login');
          return;
        }

        console.error('Error fetching notifications:', error);
        console.error('Notifications error details:', error.response?.data);
        console.error('Notifications error status:', status);
      }
      setLoadError(error instanceof Error ? error.message : 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, logout, navigate, t]);
  
  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      setLoadError(null);
      return;
    }

    fetchNotifications();
    
    // Настройваме интервал за периодично обновяване на известията
    const interval = setInterval(fetchNotifications, 60000); // Обновяваме на всяка минута
    
    // Настройваме WebSocket слушател за нови известия
    if (socket) {
      const messageHandler = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'notification') {
            // Добавяме новото известие към списъка
            const newNotification = data.notification as Notification;
            setNotifications(prev => [newNotification, ...prev]);
            setUnreadCount(prev => prev + 1);
            
            // Показваме нативно известие в браузъра, ако потребителят е дал разрешение.
            // ВАЖНО: window.Notification изобщо не съществува в обикновен таб на
            // iOS Safari/Chrome (WebKit) - директен достъп до `Notification.*`
            // хвърля `ReferenceError: Can't find variable: Notification`, което
            // (тъй като е вътре в useEffect) чупи целия render tree -> бял екран
            // само на iOS. Затова винаги проверяваме `isNotificationApiSupported()` първо.
            if (isNotificationApiSupported() && Notification.permission === 'granted') {
              const title = newNotification.title;
              const options = {
                body: newNotification.message,
                icon: '/logo192.png',
              };
              new Notification(title, options);
            }
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      socket.addEventListener('message', messageHandler);
      
      // Искаме разрешение за показване на нативни известия в браузъра (само ако API-то съществува)
      if (isNotificationApiSupported() && Notification.permission !== 'granted' && Notification.permission !== 'denied') {
        Notification.requestPermission();
      }
      
      return () => {
        clearInterval(interval);
        socket.removeEventListener('message', messageHandler);
      };
    } else {
      // Искаме разрешение за показване на нативни известия в браузъра (само ако API-то съществува)
      if (isNotificationApiSupported() && Notification.permission !== 'granted' && Notification.permission !== 'denied') {
        Notification.requestPermission();
      }
      
      return () => {
        clearInterval(interval);
      };
    }
  }, [socket, fetchNotifications, isAuthenticated]);
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleNotificationClick = async (notification: Notification) => {
    // Маркираме известието като прочетено
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    
    // Навигираме към съответната страница
    if (notification.link) {
      navigate(notification.link);
    } else if ((notification.type === 'message_received' || notification.type === 'chat_started') && notification.sender_id) {
      navigate(`/messages/${notification.sender_id}`);
    } else if (notification.task_id) {
      navigate(`/tasks/${notification.task_id}`);
    }
    
    handleClose();
  };
  
  const markAsRead = async (id: number) => {
    try {
      await api.post(`/api/v1/notifications/${id}/read`);
      
      // Обновяваме локалния списък с известия
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === id 
            ? { ...notification, is_read: 1 } 
            : notification
        )
      );
      
      // Намаляваме брояча на непрочетените известия
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };
  
  const markAllAsRead = async () => {
    try {
      await api.post('/api/v1/notifications/read-all');
      
      // Обновяваме локалния списък с известия
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, is_read: 1 }))
      );
      
      // Нулираме брояча на непрочетените известия
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };
  
  const getLocale = () => getDateFnsLocale(i18n.language);
  
  const formatDate = (date: string) => {
    return format(new Date(date), 'dd MMM yyyy HH:mm', { locale: getLocale() });
  };
  
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_bid':
      case 'task_created':
        return <AssignmentIcon color="primary" />;
      case 'bid_accepted':
        return <CheckIcon color="success" />;
      case 'bid_rejected':
        return <CloseIcon color="error" />;
      case 'task_completed':
        return <CheckIcon color="success" />;
      case 'review_received':
        return <StarIcon color="warning" />;
      case 'message_received':
        return <EmailIcon color="info" />;
      case 'chat_started':
        return <ChatIcon color="info" />;
      default:
        return <NotificationsIcon color="action" />;
    }
  };

  const getLocalizedNotificationContent = (notification: Notification) => {
    const typeKey = notification.type;
    const localizedTitle = t(`notifications.types.${typeKey}`, notification.title);
    const localizedMessage = t(`notifications.messages.${typeKey}`, {
      defaultValue: notification.message,
      taskTitle: notification.title,
    });

    return {
      title: localizedTitle,
      message: localizedMessage,
    };
  };
  
  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label={t('notifications.title')}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: '80vh',
            width: '350px',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{t('notifications.title')}</Typography>
          {unreadCount > 0 && (
            <Tooltip title={t('notifications.mark_all_read')}>
              <IconButton size="small" onClick={markAllAsRead}>
                <MarkChatReadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        <Divider />
        
        {loading && notifications.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : loadError ? (
          <MenuItem sx={{ color: 'error.main', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="body2">{t('notifications.error')}</Typography>
            <Typography variant="caption">{loadError}</Typography>
            <Button 
              size="small" 
              variant="outlined" 
              color="primary" 
              onClick={fetchNotifications} 
              sx={{ mt: 1 }}
            >
              {t('notifications.retry')}
            </Button>
          </MenuItem>
        ) : !isConnected && notifications.length === 0 ? (
          // ВАЖНО: показваме "disconnected" само когато няма и заредени
          // известия от REST - иначе скриваме валиден списък само защото
          // WebSocket-ът временно не е свързан (real-time push отпада, но
          // историята от базата вече е налична).
          <MenuItem sx={{ color: 'warning.main', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="body2">{t('notifications.disconnected')}</Typography>
            {lastError && <Typography variant="caption">{lastError}</Typography>}
            <Button 
              size="small" 
              variant="outlined" 
              color="primary" 
              onClick={reconnect} 
              sx={{ mt: 1 }}
            >
              {t('notifications.reconnect')}
            </Button>
          </MenuItem>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('notifications.no_notifications')}
            </Typography>
          </Box>
        ) : (
          <>
            {!isConnected && (
              <Box sx={{ px: 2, py: 0.5, bgcolor: 'warning.light' }}>
                <Typography variant="caption" color="warning.dark">
                  {t('notifications.disconnected')}
                </Typography>
              </Box>
            )}
            {notifications.map((notification) => {
            const localizedContent = getLocalizedNotificationContent(notification);

            return (
              <MenuItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderLeft: notification.is_read ? 'none' : '4px solid',
                  borderColor: 'primary.main',
                  bgcolor: notification.is_read ? 'transparent' : 'action.hover',
                }}
              >
                <ListItemIcon>
                  {notification.sender_id ? (
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {notification.sender_name?.charAt(0) || '?'}
                    </Avatar>
                  ) : (
                    getNotificationIcon(notification.type)
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={localizedContent.title}
                  secondary={
                    <React.Fragment>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.primary"
                        sx={{ display: 'block' }}
                      >
                        {localizedContent.message}
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        {formatDate(notification.created_at)}
                      </Typography>
                    </React.Fragment>
                  }
                />
              </MenuItem>
            );
            })}
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell; 